import {SystemsEngineerEvent} from '@features/systems-engineer-dashboard'
import {trackEvent, TrackEventData} from '@hconnect/common/logging/Analytics'
import {useUserContext} from '@hconnect/login'
import {getPlantName, useUpmPlantsContext} from '@hconnect/upm'
import {isObject} from 'lodash'
import {useCallback} from 'react'
import {useParams} from 'react-router'

import {DowntimeAnalyticsEvent} from './trackDowntimeAnalyticEvents'
import {JobsToBeDoneReportingEvent} from './trackJobsToBeDoneReportingEvents'
import {OptimizationSpecialistEvent} from './trackOptimizationSpecialistEvents'
import {PerformanceReviewPageEvent} from './trackPerformanceReviewPageEvents'
import {ProcessEngineerEvent} from './trackProcessEngineerEvents'
import {RcfaComplianceGeneralEvent} from './trackRcfaComplianceEvents'
import {ReliabilityAnalyticsEvent} from './trackReliabilityAnalyticEvents'
import {ServiceLevelAgreementsEvent} from './trackServiceLevelAgreementsEvents'

export enum Feature {
  DowntimeDashboard = 'Downtime Dashboard',
  JobsToBeDoneReporting = 'Jobs To Be Done Reporting',
  ReliabilityDashboard = 'Reliability Engineer Dashboard',
  ServiceLevelAgreements = 'Service Level Agreements',
  SystemsEngineer = 'Systems Engineer',
  ProcessEngineer = 'Process Engineer',
  RCFACompliance = 'RCFA Compliance',
  OptimizationSpecialist = 'Optimization Specialist',
  PerformanceReviewPage = 'Performance Review Page'
}

interface CommonAnalyticsPlantData {
  data: {
    timestamp: string
    userId: string
  }
}

interface CommonAnalyticsData extends CommonAnalyticsPlantData {
  data: CommonAnalyticsPlantData['data'] & {
    plantId: string
    plantName: string
    country: string
  }
}

type AnalyticsEvent =
  | ((
      | DowntimeAnalyticsEvent
      | OptimizationSpecialistEvent
      | PerformanceReviewPageEvent
      | SystemsEngineerEvent
      | ServiceLevelAgreementsEvent
      | ProcessEngineerEvent
      | ReliabilityAnalyticsEvent
      | JobsToBeDoneReportingEvent
    ) & {
      data: CommonAnalyticsData['data']
    })
  | (RcfaComplianceGeneralEvent & {
      data: CommonAnalyticsPlantData['data']
    })

enum EventType {
  Global = 'Global',
  PlantSpecific = 'PlantSpecific'
}

export enum SubProduct {
  HROC = 'hroc',
  COCKPIT = 'cockpit'
}

export const Product = 'hproduce'

export const featureToSubProductMap: {[key in Feature]: SubProduct} = {
  [Feature.DowntimeDashboard]: SubProduct.COCKPIT,
  [Feature.JobsToBeDoneReporting]: SubProduct.HROC,
  [Feature.ReliabilityDashboard]: SubProduct.HROC,
  [Feature.ServiceLevelAgreements]: SubProduct.HROC,
  [Feature.SystemsEngineer]: SubProduct.HROC,
  [Feature.ProcessEngineer]: SubProduct.HROC,
  [Feature.RCFACompliance]: SubProduct.HROC,
  [Feature.OptimizationSpecialist]: SubProduct.HROC,
  [Feature.PerformanceReviewPage]: SubProduct.HROC
}

export const trackAnalyticsEvent = <T extends AnalyticsEvent['name']>(
  eventName: T,
  subProduct: SubProduct,
  data: TrackEventData
) => trackEvent(eventName, {product: Product, subProduct, ...data})

type PathParameter = {
  plantId: string
}

export const useTrackAnalyticsEvent = <V>(feature: Feature, eventType: EventType) => {
  const {plantId} = useParams<PathParameter>()
  const upmPlants = useUpmPlantsContext()
  const {loggedUser} = useUserContext()
  const userId = loggedUser?.user_id
  const country = loggedUser?.country

  return useCallback(
    <T extends AnalyticsEvent['name']>(
      eventName: T,
      data: Extract<V, {name: T}> extends {data: infer D} ? D : Record<string, never>
    ) => {
      const globalEventData = {
        timestamp: new Date().toISOString(),
        userId,
        country,
        feature,
        ...(isObject(data) ? data : {})
      }

      if (eventType === EventType.Global) {
        trackAnalyticsEvent(eventName, featureToSubProductMap[feature], globalEventData)
      } else if (eventType === EventType.PlantSpecific) {
        if (plantId) {
          const plantName = getPlantName(upmPlants, plantId)
          trackAnalyticsEvent(eventName, featureToSubProductMap[feature], {
            plantId,
            plantName,
            ...globalEventData
          })
        }
      } else {
        throw new Error('Unknown eventType')
      }
    },
    [feature, plantId, userId, country, upmPlants, eventType]
  )
}

export const useTrackDowntimeAnalyticsEvent = () =>
  useTrackAnalyticsEvent<DowntimeAnalyticsEvent>(Feature.DowntimeDashboard, EventType.PlantSpecific)

export const useTrackOptimizationSpecialistAnalyticsEvent = () =>
  useTrackAnalyticsEvent<OptimizationSpecialistEvent>(
    Feature.OptimizationSpecialist,
    EventType.PlantSpecific
  )

export const useTrackPerformanceReviewPageAnalyticsEvent = () =>
  useTrackAnalyticsEvent<PerformanceReviewPageEvent>(
    Feature.PerformanceReviewPage,
    EventType.PlantSpecific
  )

export const useTrackRcfaComplianceAnalyticsGeneralEvent = () =>
  useTrackAnalyticsEvent<RcfaComplianceGeneralEvent>(Feature.RCFACompliance, EventType.Global)

export const useTrackProcessEngineerAnalyticsEvent = () =>
  useTrackAnalyticsEvent<ProcessEngineerEvent>(Feature.ProcessEngineer, EventType.PlantSpecific)

export const useTrackSystemsEngineerAnalyticsEvent = () =>
  useTrackAnalyticsEvent<SystemsEngineerEvent>(Feature.SystemsEngineer, EventType.PlantSpecific)

export const useTrackJobsToBeDoneReportingAnalyticsEvent = () =>
  useTrackAnalyticsEvent<JobsToBeDoneReportingEvent>(
    Feature.JobsToBeDoneReporting,
    EventType.PlantSpecific
  )

export const useTrackServiceLevelAgreementsAnalyticsEvent = () =>
  useTrackAnalyticsEvent<ServiceLevelAgreementsEvent>(
    Feature.ServiceLevelAgreements,
    EventType.PlantSpecific
  )

export const useTrackReliabilityAnalyticsEvent = () =>
  useTrackAnalyticsEvent<ReliabilityAnalyticsEvent>(
    Feature.ReliabilityDashboard,
    EventType.PlantSpecific
  )
